import React, { useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandItem,
} from "@/components/ui/command";
import { FitText } from "@/components/FitText";

const matchName = (value, search) => {
  const name = value.split("||")[0].toLowerCase();
  return name.includes(search.trim().toLowerCase()) ? 1 : 0;
};

export function ArmyCombobox({ armies, armyKeys, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const selectedName = armies[value]?.armyName;

  const handleOpenChange = (next) => {
    setOpen(next);
    if (!next) setSearch("");
  };

  const handleTriggerKeyDown = (e) => {
    if (open || e.key.length !== 1 || e.key === " " || e.ctrlKey || e.metaKey || e.altKey) return;
    e.preventDefault();
    setSearch(e.key);
    setOpen(true);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id="army-select"
          role="combobox"
          aria-expanded={open}
          data-testid="army-select"
          data-placeholder={selectedName ? undefined : ""}
          onKeyDown={handleTriggerKeyDown}
          className={cn(
            "flex w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent shadow-sm focus:outline-none",
            "sp-select-trigger sp-army-trigger h-auto border px-4 py-2 text-base focus:ring-1 focus:ring-[#8A734C] min-w-[280px] cursor-pointer"
          )}
        >
          <span>{selectedName ? <FitText text={selectedName} /> : "— Select an army —"}</span>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="sp-select-content p-0 w-max min-w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-2rem)] bg-[#FAF6ED] border-[#8A734C] text-[#16120E]"
      >
        <Command filter={matchName} className="bg-transparent text-[#16120E]">
          <CommandInput
            autoFocus
            data-testid="army-search-input"
            placeholder="Search armies…"
            value={search}
            onValueChange={setSearch}
            className="text-[#16120E]"
          />
          <CommandList className="max-h-[60vh]">
            <CommandEmpty>No armies found.</CommandEmpty>
            {armyKeys.map((k) => {
              const selected = k === value;
              return (
                <CommandItem
                  key={k}
                  value={`${armies[k].armyName}||${k}`}
                  data-testid={`army-option-${k}`}
                  onSelect={() => {
                    onChange(k);
                    handleOpenChange(false);
                  }}
                  className={cn(
                    "sp-select-item whitespace-nowrap pr-8 text-base cursor-pointer data-[selected=true]:bg-[#8A734C] data-[selected=true]:text-[#FAF6ED]",
                    selected ? "bg-[#8A734C] text-[#FAF6ED]" : "text-[#16120E]"
                  )}
                >
                  {armies[k].armyName}
                  {selected && <Check className="absolute right-2 h-4 w-4" />}
                </CommandItem>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
